import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const BoostModal = ({ isOpen, onClose, postId, token, onSuccess }) => {
  const [bountyAmount, setBountyAmount] = useState('100');
  const [boostDuration, setBoostDuration] = useState('24');
  const [boosting, setBoosting] = useState(false);

  const handleBoost = async () => {
    if (!bountyAmount || parseFloat(bountyAmount) <= 0) {
      toast.error('Please enter a valid bounty amount');
      return;
    }

    try {
      setBoosting(true);
      await axios.post(
        `${API}/business/posts/${postId}/boost`,
        {
          bounty_amount: parseFloat(bountyAmount),
          boost_duration_hours: parseInt(boostDuration)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`Post boosted with $${bountyAmount} bounty for ${boostDuration} hours!`);
      onSuccess();
      onClose();
      setBountyAmount('100');
      setBoostDuration('24');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to boost post');
    } finally {
      setBoosting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="boost-modal">
        <DialogHeader>
          <DialogTitle>🚀 Boost Your Post</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-brand-gold/10 border border-brand-gold/20">
            <p className="text-sm text-brand-text">
              Boost your post to jump to the top of salesperson feeds and attract top talent faster!
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bounty">Bounty Amount ($)</Label>
            <Input
              id="bounty"
              type="number"
              min="0"
              step="10"
              value={bountyAmount}
              onChange={(e) => setBountyAmount(e.target.value)}
              placeholder="100"
              data-testid="boost-bounty-input"
            />
            <p className="text-xs text-brand-muted">
              Additional bonus for the salesperson who fulfills this opportunity
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">Boost Duration</Label>
            <Select value={boostDuration} onValueChange={setBoostDuration}>
              <SelectTrigger data-testid="boost-duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="12">12 hours</SelectItem>
                <SelectItem value="24">24 hours</SelectItem>
                <SelectItem value="48">2 days</SelectItem>
                <SelectItem value="72">3 days</SelectItem>
                <SelectItem value="168">1 week</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Preview */}
          <div className="p-4 rounded-lg bg-brand-bg border border-zinc-800">
            <div className="text-sm font-medium text-brand-text mb-2">Boost Preview</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-brand-muted">Bounty:</span>
                <span className="text-brand-gold font-bold">${bountyAmount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-muted">Duration:</span>
                <span className="text-brand-text">{boostDuration} hours</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-zinc-800">
                <span className="text-brand-text font-medium">Total Investment:</span>
                <span className="text-brand-gold font-bold">${bountyAmount}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={boosting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBoost}
              className="flex-1 bg-brand-gold hover:bg-brand-gold/90 text-black"
              disabled={boosting}
              data-testid="boost-confirm"
            >
              {boosting ? 'Boosting...' : `Boost for $${bountyAmount}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BoostModal;
